/* eslint-disable @nfq/no-magic-numbers */
import type {Ref} from 'react';

import styled from '@emotion/styled';
import {media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H4} from 'UI/components/layout/Text';

import {SequenceCardAnim} from 'UI/animations/sequence';

import {useSequenceItem} from './useSequenceItem';
import {transient} from 'Application/configs/emotionTransientProps';

import type {SequenceBlockType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `SequenceItem` component.
 * This interface specifies all the required and optional properties needed to render an individual sequence timeline item.
 * It includes content data, layout positioning flags, styling options, and accessibility properties for comprehensive component configuration.
 */
interface ComponentProps {
    /**
     * The rich text description content for the sequence item that will be rendered using the PayloadLexical component.
     * This property contains structured content data from Payload CMS that supports various rich text formatting options.
     * The description provides detailed information about the sequence item and is displayed within the content card area.
     */
    description: SequenceBlockType['contents'][number]['description'];
    /**
     * The image data for the sequence item icon that will be displayed within the circular icon wrapper.
     * This property contains image metadata from Payload CMS including source URLs, alt text, and sizing information.
     * The image serves as a visual identifier for the sequence item and is rendered using the PayloadImage component.
     */
    image: SequenceBlockType['contents'][number]['image'];
    /**
     * Optional flag to determine the positioning of the content card in the grid layout for creating alternating visual arrangement.
     * When true, the card will be positioned on the left side in desktop view, otherwise it appears on the right side.
     * This property enables the creation of a zigzag timeline layout where items alternate between left and right positioning.
     *
     * @default false
     */
    isEven?: boolean;
    /**
     * The title text for the sequence item that will be rendered as an H4 element with h3 semantic meaning.
     * This property contains the main heading text that describes or names the sequence item step or milestone.
     * The title appears prominently at the top of the content card and provides context for the sequence item.
     */
    itemTitle: SequenceBlockType['contents'][number]['title'];
    /**
     * Optional React ref to access the underlying HTMLLIElement for DOM manipulation, measurements, or scroll positioning.
     * This property allows parent components to directly interact with the sequence item's DOM element when needed.
     * The ref is particularly useful for implementing scroll-based animations or programmatic focus management.
     */
    ref?: Ref<HTMLLIElement>;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     * The identifier is used in automated tests and debugging scenarios to locate and interact with the component.
     *
     * @default 'SequenceItem'
     */
    testId?: string;
}

/**
 * The `SequenceItem` component renders an individual item within a sequence timeline layout, displaying content such as an image, title, and description.
 * This component is designed to work within a grid-based sequence layout where items can be positioned alternately on different sides based on the `isEven` prop.
 * It uses motion animations for enhanced user experience and integrates with MobX for reactive state management through the observer pattern.
 *
 * @param props             The component props.
 * @param props.description The rich text description content for the sequence item, rendered using PayloadLexical component.
 * @param props.image       The image data for the sequence item icon, displayed within the circular icon wrapper.
 * @param props.isEven      Optional flag to determine the positioning of the card in the grid layout for alternating sides.
 * @param props.itemTitle   The title text for the sequence item, rendered as an H4 element with h3 semantic meaning.
 * @param props.ref         Optional React ref to access the underlying HTMLLIElement for DOM manipulation or measurements.
 * @param props.testId      Test identifier for component testing and automation purposes.
 * @returns The rendered sequence item JSX element with animated icon and content card.
 *
 * @example
 * ```tsx
 * <SequenceItem
 *     description={richTextContent}
 *     image={imageData}
 *     isEven={true}
 *     itemTitle="Project Milestone"
 *     testId="sequence-item-1"
 * />
 * ```
 */
const SequenceItem = observer(({
    description,
    image,
    isEven = false,
    itemTitle,
    ref,
    testId = 'SequenceItem'
}: ComponentProps) => {
    const {bgRef, bgSize} = useSequenceItem();

    return (
        <Wrapper ref={ref} data-cy={testId}>
            <SequenceIconWrapper ref={bgRef} style={{backgroundSize: bgSize}}>
                <SequenceIconWrapperBg>
                    {image && (<SequenceIcon expectedSizes={{md: 110, xs: 40}} image={image} />)}
                </SequenceIconWrapperBg>
            </SequenceIconWrapper>
            <Card
                $isEven={isEven}
                animate="exit"
                initial="initial"
                variants={SequenceCardAnim}
                viewport={{margin: '-21% -20% -20% -20%'}}
                whileInView="enter"
            >
                {itemTitle && (
                    <>
                        <H4 as="h3">{itemTitle}</H4>
                        <Spacer y={2} isNotStretching />
                    </>
                )}
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {description && (
                    <PayloadLexical description={description} />
                )}
            </Card>
        </Wrapper>
    );
});

SequenceItem.displayName = 'SequenceItem';

export {SequenceItem};

const Wrapper = styled.li`
    align-items: center;
    appearance: none;
    display: grid;
    grid-column: 1 / 3;
    grid-row: auto;
    grid-template-columns: subgrid;
    grid-template-rows: subgrid;
    padding-block-end: 10rem;
    position: relative;

    ${media('md')} {
        grid-column: 1 / 4;
    }
`;

const SequenceIconWrapper = styled(motion.div)`
    align-items: center;
    background-color: ${({theme}) => theme.colors.neutral};
    background-image: linear-gradient(to bottom, ${({theme}) => theme.colors.primary} 0%, ${({theme}) => theme.colors.primary} 100%);
    background-repeat: no-repeat;
    background-size: 100% 0%;
    border-radius: 50%;
    display: flex;
    grid-column: 1;
    height: 8rem;
    justify-content: center;
    width: 8rem;

    ${media('md')} {
        grid-column: 2;
        height: 12rem;
        width: 12rem;
    }
`;

const SequenceIconWrapperBg = styled.div`
    align-items: center;
    background-color: ${({theme}) => theme.colors.backgroundDark};
    border-radius: 50%;
    display: flex;
    height: 7rem;
    justify-content: center;
    width: 7rem;

    ${media('md')} {
        height: 11rem;
        width: 11rem;
    }
`;

const SequenceIcon = styled(PayloadImage, transient)`
    max-height: 5rem;
    max-width: 5rem;

    ${media('md')} {
        max-height: 8rem;
        max-width: 8rem;
    }
`;

interface CardProps {
    $isEven?: boolean;
}

const Card = styled(motion.div, transient)<CardProps>`
    background-color: hsl(from ${({theme}) => theme.colors.primary} h s l / 0.2);
    border-radius: 16px;
    grid-column: 2 / -1;
    padding: ${spacing(4)};

    ${media('md')} {
        grid-column: ${({$isEven}) => ($isEven ? 1 : 3)};
        padding: ${spacing(7)};
    }
`;